import React from 'react';
import PropTypes from 'prop-types';
import './index.css';
import UploadIcon from './UploadIcon.svg';

const ERROR = {
  NOT_SUPPORTED_EXTENSION: 'NOT_SUPPORTED_EXTENSION',
  FILESIZE_TOO_LARGE: 'FILESIZE_TOO_LARGE',
};

class ImageUploaderLike extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pictures: [...props.defaultImages],
      files: [],
      fileErrors: [],
    };
    this.inputElement = null;
  }

  componentDidUpdate(prevProps, prevState) {
    // Match original behavior: call onChange whenever files changes
    if (prevState.files !== this.state.files) {
      this.props.onChange(this.state.files, this.state.pictures);
    }
  }

  componentWillUnmount() {
    // Clean up blob URLs to avoid memory leaks
    this.state.pictures.forEach((url) => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }

  hasExtension(fileName) {
    const pattern = '(' + this.props.imgExtension.join('|').replace(/\./g, '\\.') + ')$';
    return new RegExp(pattern, 'i').test(fileName);
  }

  onDropFile = (e) => {
    const files = e.target.files;
    const allValidFiles = [];
    const fileErrors = [];

    if (!files || files.length === 0) {
      this.setState({ fileErrors: [] });
      return;
    }

    const list = this.props.singleImage ? [files[0]] : Array.from(files);

    list.forEach((file) => {
      let fileError = { name: file.name };

      if (!this.hasExtension(file.name)) {
        fileErrors.push({ ...fileError, type: ERROR.NOT_SUPPORTED_EXTENSION });
        return;
      }

      if (file.size > this.props.maxFileSize) {
        fileErrors.push({ ...fileError, type: ERROR.FILESIZE_TOO_LARGE });
        return;
      }

      allValidFiles.push(file);
    });

    // clear previous previews if singleImage
    if (this.props.singleImage) {
      this.state.pictures.forEach((url) => {
        if (typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    }

    const nextPictures = this.props.singleImage ? [] : this.state.pictures.slice();
    const nextFiles = this.props.singleImage ? [] : this.state.files.slice();

    allValidFiles.forEach((file) => {
      const previewUrl = URL.createObjectURL(file);
      nextPictures.push(previewUrl);
      nextFiles.push(file);
    });

    this.setState({
      fileErrors,
      pictures: nextPictures,
      files: nextFiles,
    });
  };

  onUploadClick = (e) => {
    // Allows selecting the same file twice in a row
    e.target.value = null;
  };

  removeImage = (picture) => {
    const removeIndex = this.state.pictures.findIndex((p) => p === picture);
    const filteredPictures = this.state.pictures.filter((_, i) => i !== removeIndex);
    const filteredFiles = this.state.files.filter((_, i) => i !== removeIndex);

    if (typeof picture === 'string' && picture.startsWith('blob:')) {
      URL.revokeObjectURL(picture);
    }

    this.setState(
      { pictures: filteredPictures, files: filteredFiles },
      () => this.props.onChange(this.state.files, this.state.pictures)
    );
  };

  triggerFileUpload = () => {
    if (this.inputElement) this.inputElement.click();
  };

  renderErrors() {
    return this.state.fileErrors.map((fileError, index) => (
      <div
        className={'errorMessage ' + this.props.errorClass}
        key={index}
        style={this.props.errorStyle}
      >
        * {fileError.name}{' '}
        {fileError.type === ERROR.FILESIZE_TOO_LARGE
          ? this.props.fileSizeError
          : this.props.fileTypeError}
      </div>
    ));
  }

  renderIcon() {
    if (!this.props.withIcon) return null;
    return <img src={UploadIcon} className="uploadIcon" alt="Upload Icon" />;
  }

  renderLabel() {
    if (!this.props.withLabel) return null;
    return (
      <p className={this.props.labelClass} style={this.props.labelStyles}>
        {this.props.label}
      </p>
    );
  }

  renderPreviewPictures() {
    return this.state.pictures.map((picture, index) => (
      <div key={index} className="uploadPictureContainer">
        <div className="deleteImage" onClick={() => this.removeImage(picture)}>
          X
        </div>
        <img src={picture} className="uploadPicture" alt="preview" />
      </div>
    ));
  }

  renderPreview() {
    return (
      <div className="uploadPicturesWrapper">
        <div className="flipMove">{this.renderPreviewPictures()}</div>
      </div>
    );
  }

  render() {
    return (
      <div className={'fileUploader ' + this.props.className} style={this.props.style}>
        <div className="fileContainer" style={this.props.fileContainerStyle}>
          {this.renderIcon()}
          {this.renderLabel()}

          <div className="errorsContainer">{this.renderErrors()}</div>

          <button
            type={this.props.buttonType}
            className={'chooseFileButton ' + this.props.buttonClassName}
            style={this.props.buttonStyles}
            onClick={this.triggerFileUpload}
          >
            {this.props.buttonText}
          </button>

          <input
            type="file"
            ref={(input) => (this.inputElement = input)}
            name={this.props.name}
            multiple={!this.props.singleImage}
            onChange={this.onDropFile}
            onClick={this.onUploadClick}
            accept={this.props.accept}
          />

          {this.props.withPreview ? this.renderPreview() : null}
        </div>
      </div>
    );
  }
}

ImageUploaderLike.defaultProps = {
  className: '',
  fileContainerStyle: {},
  buttonClassName: '',
  buttonStyles: {},
  withPreview: false,
  accept: 'image/*',
  name: '',
  withIcon: true,
  buttonText: 'Choose images',
  buttonType: 'button',
  withLabel: true,
  label: 'Max file size: 5mb, accepted: jpg|gif|png',
  labelStyles: {},
  labelClass: '',
  imgExtension: ['.jpg', '.jpeg', '.gif', '.png'],
  maxFileSize: 5242880,
  fileSizeError: ' file size is too big',
  fileTypeError: ' is not a supported file extension',
  errorClass: '',
  style: {},
  errorStyle: {},
  singleImage: false,
  onChange: () => {},
  defaultImages: [],
};

ImageUploaderLike.propTypes = {
  style: PropTypes.object,
  fileContainerStyle: PropTypes.object,
  className: PropTypes.string,
  onChange: PropTypes.func,
  buttonClassName: PropTypes.string,
  buttonStyles: PropTypes.object,
  buttonType: PropTypes.string,
  withPreview: PropTypes.bool,
  accept: PropTypes.string,
  name: PropTypes.string,
  withIcon: PropTypes.bool,
  buttonText: PropTypes.string,
  withLabel: PropTypes.bool,
  label: PropTypes.string,
  labelStyles: PropTypes.object,
  labelClass: PropTypes.string,
  imgExtension: PropTypes.array,
  maxFileSize: PropTypes.number,
  fileSizeError: PropTypes.string,
  fileTypeError: PropTypes.string,
  errorClass: PropTypes.string,
  errorStyle: PropTypes.object,
  singleImage: PropTypes.bool,
  defaultImages: PropTypes.array,
};

export default ImageUploaderLike;
